/**
 * Metade de ENTRADA do exame eletrônico: o protocolo transcrito (E74.F1; E78.F2).
 *
 * Espelha `apps/api/types/exame_eletronico_protocolo.py`, inclusive na divisão:
 * o protocolo do CASO (`ProtocoloAssinaturaCasoData`) e o que é da PEÇA
 * (`CodigoDeclaradoInput`) desde a ADR-0077. Separado da metade de saída pelo
 * mesmo motivo do backend — o arquivo único passou do teto de 400 linhas.
 */
import type { LidoDosDocumentosData } from '@/lib/exame_eletronico_lido_dos_documentos';

export type TipoAssinatura = 'qualificada_icp' | 'avancada' | 'simples' | 'nao_informado';

/**
 * Como a plataforma imprime a data no comprovante (E84.F5, GUS-44 RF01).
 *
 * O comprovante do banco de referência imprime o corpo em mês/dia
 * ("Contratado em 07/05/2023" = 5 de julho) enquanto o resto do Brasil lê
 * dia/mês. É ele — nunca o metadado do arquivo, que é editável — que resolve a
 * leitura da data do ato.
 */
export type FormatoDaData = 'dia_mes' | 'mes_dia';

export const FORMATOS_DA_DATA: readonly FormatoDaData[] = ['dia_mes', 'mes_dia'];
export type FonteCoordenada =
  | 'gps'
  | 'wifi'
  | 'rede_celular'
  | 'navegador'
  | 'ip'
  | 'nao_informada';
export type TipoEventoProtocolo =
  | 'envio_do_link'
  | 'autenticacao_de_acesso'
  | 'visualizacao'
  | 'aceite'
  | 'autenticacao'
  | 'biometria'
  | 'assinatura'
  | 'biometria_de_fechamento'
  | 'aprovacao'
  | 'liberacao';
/** E162.F1.T07 — o papel é o que a fonte rotula; "IP" solto é `nao_declarado`. */
export type PapelIp = 'cliente' | 'servidor' | 'nao_declarado';
/** O que a peça é, deduzido do rótulo do hash que a cobriu no comprovante (E84.F1). */
export type TipoDaPeca =
  | 'ccb'
  | 'cet'
  | 'termo_inss'
  | 'cadastro'
  | 'termo_ciencia'
  | 'proposta_de_seguro'
  | TipoDeApoio
  | 'termo_de_servico'
  | 'crlv'
  | 'foto_do_veiculo'
  | 'laudo_de_formalizacao'
  | 'termo_de_adesao'
  | 'termo_de_consentimento'
  | 'termo_de_desbloqueio'
  | 'outro';

/** E98.F5: os documentos de apoio, que o perito escolhe (espelha `types/documento_de_apoio.py`). */
export type TipoDeApoio =
  | 'documento_identidade'
  | 'cnh'
  // E116.F1: o passaporte e a identidade de estrangeiro (CRNM/RNE).
  | 'passaporte'
  | 'identidade_de_estrangeiro'
  | 'cnh_verso'
  | 'nota_fiscal'
  | 'extrato_bancario'
  | 'fatura_de_cartao'
  // E101.F4: o CRLV e a fotografia do veículo do dossiê em imagem.
  | 'crlv'
  | 'foto_do_veiculo';

export const TIPOS_ASSINATURA: readonly TipoAssinatura[] = [
  'nao_informado',
  'qualificada_icp',
  'avancada',
  'simples',
];
export const FONTES_COORDENADA: readonly FonteCoordenada[] = [
  'nao_informada',
  'gps',
  'wifi',
  'rede_celular',
  'navegador',
  'ip',
];
export const TIPOS_EVENTO: readonly TipoEventoProtocolo[] = [
  'envio_do_link',
  'autenticacao_de_acesso',
  'visualizacao',
  'aceite',
  'autenticacao',
  'biometria',
  'assinatura',
  'biometria_de_fechamento',
  'aprovacao',
  'liberacao',
];
export const PAPEIS_IP: readonly PapelIp[] = ['cliente', 'servidor', 'nao_declarado'];

export interface SignatarioProtocoloData {
  nome?: string | null;
  cpf?: string | null;
  email?: string | null;
  telefone?: string | null;
}

export interface EventoProtocoloData {
  tipo: TipoEventoProtocolo;
  em_utc?: string | null;
  em_local?: string | null;
  descricao?: string | null;
}

export interface IpProtocoloData {
  endereco: string;
  papel: PapelIp;
  /** E112.F3 (C-05, D-10): a porta é a do servidor (destino); a de origem não é registrada. */
  porta_de_destino?: string | null;
}

export interface CoordenadaProtocoloData {
  latitude: number;
  longitude: number;
  precisao_m?: number | null;
  fonte: FonteCoordenada;
  em?: string | null;
}

export interface DispositivoProtocoloData {
  user_agent?: string | null;
  fingerprint?: string | null;
}

export interface BiometriaProtocoloData {
  presente: boolean;
  sessao_id?: string | null;
  score?: number | null;
  threshold?: number | null;
  prova_de_vida?: boolean | null;
  fornecedor?: string | null;
  /** E98.F2 — como o relatório do provedor declara que a amostra foi coletada. */
  modulo_de_coleta?: string | null;
  coleta_por_reaproveitamento?: boolean | null;
  documento_validado?: boolean | null;
  /** E101.F3 — a transação biométrica que a página de assinaturas declara. */
  transacao_na_assinatura?: string | null;
  /** E101.F3 — o instante da captura no painel do provedor (Brasília presumido). */
  capturada_em?: string | null;
  capturada_em_fuso_presumido?: boolean;
  autorizada?: boolean | null;
  base_consultada?: string | null;
  codigo_de_resposta?: string | null;
}

/** E98.F2 — de onde o envelope foi lido. */
export type FonteDoEnvelope = 'certificado_de_conclusao' | 'metadados_do_arquivo';
/** E98.F2 — o signatário de ordem 1 é o cliente; os demais nunca viram dado dele. */
export type PapelNoEnvelope = 'cliente' | 'outro_signatario';

export interface SignatarioDoEnvelopeData {
  ordem?: number | null;
  papel: PapelNoEnvelope;
  nome?: string | null;
  email?: string | null;
  ip?: string | null;
  enviado_em?: string | null;
  visualizado_em?: string | null;
  assinado_em?: string | null;
  autenticacao?: string | null;
}

export interface DocumentoDoEnvelopeData {
  documento_id?: string | null;
  nome: string;
  sequencia?: number | null;
}

export interface AbaPreenchidaData {
  destinatario_ordem?: number | null;
  destinatario?: string | null;
  documento_id?: string | null;
  pagina?: number | null;
  rotulo?: string | null;
  valor: string;
}

export interface CampoDoEnvelopeData {
  nome: string;
  valor: string;
}

/**
 * E98.F2 (ADR-0083 §2.3) — o envelope por papel, lido do certificado de conclusão
 * e dos metadados do arquivo. A tela não o edita, mas o "Salvar" o devolve: o
 * servidor substitui o protocolo inteiro (a lição da E84.F9).
 */
export interface EnvelopeDoProtocoloData {
  fontes: FonteDoEnvelope[];
  paginas_declaradas?: number | null;
  fuso_declarado?: string | null;
  fuso_presumido?: string | null;
  documentos: DocumentoDoEnvelopeData[];
  signatarios: SignatarioDoEnvelopeData[];
  abas: AbaPreenchidaData[];
  campos: CampoDoEnvelopeData[];
}

/**
 * O protocolo do PROCESSO (E78.F2, ADR-0077): um por caso, não por peça.
 *
 * O que a plataforma diz da formalização — signatário, trilha, IPs, coordenada,
 * biometria — vale para todas as peças assinadas na mesma contratação.
 */
export interface ProtocoloAssinaturaCasoData {
  plataforma?: string | null;
  codigo_verificacao?: string | null;
  /**
   * E93.F1 (GUS-56 RN10) — o endereço de validação IMPRESSO no comprovante.
   * Precisa ir e voltar no "Salvar": o servidor substitui o protocolo inteiro,
   * e um campo que a tela não conhece seria apagado (a lição da E84.F9).
   */
  url_verificacao?: string | null;
  /** E84.F3 — o número da proposta que amarra cédula, CET e comprovante. */
  proposta?: string | null;
  envelope_id?: string | null;
  assinatura_id?: string | null;
  tipo_assinatura: TipoAssinatura;
  /** E93.F1 — a frase literal do método, de onde a classificação declarada saiu. */
  tipo_assinatura_texto?: string | null;
  signatario: SignatarioProtocoloData;
  eventos: EventoProtocoloData[];
  /** E84.F4 — o registro da assinatura na linha de status do comprovante, com fuso. */
  assinatura_registrada_em?: string | null;
  /** E84.F4 — a leitura presumiu o sinal do fuso (glifo ilegível); editável. */
  assinatura_registrada_em_fuso_presumido?: boolean;
  /** E114.F1 (R-20) — a linha de status imprime só hora e minuto. */
  assinatura_registrada_em_sem_segundos?: boolean;
  /**
   * E84.F5 (GUS-44 RF01) — o formato DECLARADO; `null` = não declarado.
   *
   * Estava fora deste contrato até a E84.F9, e a ausência era um defeito: o GET
   * devolvia o campo, a tela montava o corpo do "Salvar" sem ele, e o
   * `upsert` do servidor SUBSTITUÍA — a âncora primária da data do ato era
   * apagada em silêncio a cada salvamento.
   */
  formato_da_data?: FormatoDaData | null;
  ips: IpProtocoloData[];
  coordenada: CoordenadaProtocoloData | null;
  dispositivo: DispositivoProtocoloData | null;
  biometria: BiometriaProtocoloData;
  envelope?: EnvelopeDoProtocoloData | null;
  /** E106.F2 — o que o arquivo imprime e a tela devolve intacto. */
  lido_dos_documentos?: LidoDosDocumentosData | null;
  observacoes?: string | null;
}

/** Entrada do motor: o protocolo do caso MAIS o código declarado da peça. */
export interface ProtocoloAssinaturaData extends ProtocoloAssinaturaCasoData {
  hash_declarado?: string | null;
}

/** O que muda por peça (E78.F2): o código declarado e as observações dela. */
export interface CodigoDeclaradoInput {
  codigo_declarado?: string | null;
  observacoes?: string | null;
  /** E84.F1 — só a reconciliação o informa; ausente mantém o gravado. */
  tipo_da_peca?: TipoDaPeca | null;
}

/** Separa do protocolo transcrito o que é do CASO — o resto é da peça. */
export function protocoloDoCaso(p: ProtocoloAssinaturaData): ProtocoloAssinaturaCasoData {
  const { hash_declarado: doPeca, ...doCaso } = p;
  void doPeca;
  return doCaso;
}
